import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ActivityService } from '../shared/activity.service';
import { ProgramGroupService } from '../shared/program-group.service';
import { ProgramGroup } from '../shared/data/program-group.data';
import { CouncilService } from '../shared/council.service';
import { Council } from '../shared/data/council.data';
import { Observable, Subscription } from 'rxjs';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { startWith, map } from 'rxjs/operators';
import { CouncilStatsService } from '../shared/council-stats.service';
import { coerceNumberProperty } from '@angular/cdk/coercion';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { AuthService, User } from '../shared/auth.service';

import { ActivityData } from '../shared/data/activity.data';

@Component({
  selector: 'app-council-summary',
  templateUrl: './council-summary.component.html',
  styleUrls: ['./council-summary.component.css'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({height: '0px', minHeight: '0'})),
      state('expanded', style({height: '*'})),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class CouncilSummaryComponent implements OnInit, OnDestroy {

  activities;
  activityData = {};
  councilStatsDoc;
  council: Council;
  displayedColumns = ['activityType', 'activityStatus'];
  expandedActivity: any | null;
  chartData: any[];
  chartOptions = {
    legend: {
      position: 'none'
    },
    hAxis: {
      title: 'Percent Completed',
      maxValue: 100,
      ticks: ActivityData.data.graphTicks
    },
    animation: {
      startup: true,
      duration: 1000,
      easing: 'out'
    }
  };
  filteredCouncils: Observable<Council[]>;
  councilForm: UntypedFormGroup;
  user: Observable<User>;
  isAdmin: boolean;
  isCommunityDirector: boolean;
  isFaithDirector: boolean;
  isFamilyDirector: boolean;
  isLifeDirector: boolean;
  isMembershipDirector: boolean;
  isAuthenticated = false;

  private subscriptions = new Subscription();

  constructor(private route: ActivatedRoute,
              private router: Router,
              private activityService: ActivityService,
              private councilService: CouncilService,
              private councilStatsService: CouncilStatsService,
              private programGroupService: ProgramGroupService,
              private authService: AuthService) {
    this.user = this.authService.getUser();
  }

  ngOnInit() {
    this.subscriptions.add(this.route.params.subscribe(params => {
      if (params.id) {
        this.subscriptions.add(this.activityService.getCouncilActivities(params.id).subscribe(res => {
          this.activities = res.map(a => {
            this.subscriptions.add(this.activityService.getActivityData(a.payload.doc.id).subscribe(activityDataRes => {
              this.activityData[a.payload.doc.id] = {...activityDataRes.payload.data() as {} };
            }));
            return {...a.payload.doc.data() as {}, id: a.payload.doc.id};
          });
        }));

        this.subscriptions.add(this.councilStatsService.getCouncilGradingStats(params.id).subscribe(res => {
          this.councilStatsDoc = res.payload;
          this.chartData = this.loadChartData();
        }));

        this.council = this.councilService.getCouncilById(Number(params.id));
      } else {
        this.councilForm = new UntypedFormGroup({
          council: new UntypedFormControl(),
        });

        this.filteredCouncils = this.councilForm.get('council').valueChanges
          .pipe(
            startWith<string | Council>(''),
            map(value => typeof value === 'string' ? value : value.id),
            map(council => council ? this._filterCouncils(council) : this.getCouncils().slice())
          );
      }
    }));

    this.subscriptions.add(this.user.subscribe(user => {
      if (user) {
        this.isAuthenticated = true;
        this.isAdmin = user.roles.admin;
        this.isCommunityDirector = user.roles.communityDirector;
        this.isFaithDirector = user.roles.faithDirector;
        this.isFamilyDirector = user.roles.familyDirector;
        this.isLifeDirector = user.roles.lifeDirector;
        this.isMembershipDirector = user.roles.membershipDirector;
      }
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  isAllowedToGrade(programGroup: ProgramGroup) {
    if (!this.isAuthenticated) {
      return false;
    }
    if (this.isAdmin) {
      return true;
    }
    switch (programGroup.id) {
      case 'faith':
        return this.isFaithDirector;
      case 'family':
        return this.isFamilyDirector;
      case 'community':
        return this.isCommunityDirector;
      case 'life':
        return this.isLifeDirector;
      case 'membership':
        return this.isMembershipDirector;
      default:
        return false;
    }
  }

  getActivityNameById(activityTypeId: string): string {
    return this.programGroupService.getActivityNameById(activityTypeId);
  }

  getProgramGroups() {
    return this.programGroupService.getProgramGroups();
  }

  getDioceseName(id: number) {
    return this.councilService.getDioceseById(id);
  }

  loadChartData(): any[] {
    const data = [];
    const programGroups = this.getProgramGroups();
    programGroups.forEach(programGroup => {
      const groupData = [];
      groupData.push(programGroup.displayName);
      let percentage = this.programGroupService.getProgramGroupTotal(programGroup.id, this.councilStatsDoc) / programGroup.totalPoints * 100;
      groupData.push(percentage);
      data.push(groupData);
    });
    return data;
    // [
    //   ['Community', 0],
    //   ['Faith', 20],
    //   ['Family', 40],
    //   ['Life', 0],
    //   ['Membership', 0],
    // ];
  }

  calculatePoints(programGroup: ProgramGroup) {
    // TODO: Figure out how to get this to work so that we don't constantly hit councilStatsDoc.
    // return this.chartData ? this.chartData.filter((val) => val[0] === programGroup.displayName)[1] : 0;
    return this.programGroupService.getProgramGroupTotal(programGroup.id, this.councilStatsDoc);
  }

  getMembershipGrowthPercentage() {
    return this.programGroupService.getMembershipGrowthPercentagePoints(this.councilStatsDoc);
  }

  activitySelected(activity) {
    // this.router.navigate(['grade-activity', activity.id]);
  }

  private _filterCouncils(value: string): Council[] {
    const filterValue = value.toLowerCase();
    return this.getCouncils()
      .filter(council => council.name.toLowerCase().indexOf(filterValue) >= 0
              || council.id.indexOf(filterValue) === 0
              || `${council.id} - ${council.name.toLowerCase()}`.indexOf(filterValue) === 0);
  }

  displayFn(council?: Council): string | undefined {
    return council ? `${council.id} - ${council.name}` : undefined;
  }

  getCouncils(): Council[] {
    return this.councilService.getCouncils();
  }

  councilSelectionChanged(event) {
    this.router.navigate(['council-summary', event.source.value.id]);
  }

  getActivityStatus(activity) {
    if (activity && this.activityData[activity.id]) {
      return this.activityData[activity.id].activityStatus;
    }
    return '';
  }

  getProgramDirectorComments(activity) {
    if (activity && this.activityData[activity.id]) {
      return this.activityData[activity.id].programDirectorComments;
    }
    return null;
  }

  getIsSupremeFeaturedProgram(activity) {
    if (activity && this.activityData[activity.id]) {
      return this.activityData[activity.id].isSupremeFeaturedProgram;
    }
    return false;
  }

  gradeActivity(activity) {
    this.router.navigate(['grade-activity', activity.id], {queryParams: {from: 'council-summary'}});
  }

  enterNewActivity(){
    window.open('https://kofcohio.org/program-reporting', '_blank');
  }
}
